// BCM-898 · Goods Receipt Dashboard — shared header strip for every Action section
// that shows tabs and action buttons together (PA1/PA2/PA3 doc review, PA4/PA6).
// Layout: the tab strip owns the left half of the row and scrolls horizontally —
// chevron buttons appear only when it overflows — while the buttons own the right
// half, so they never wrap underneath the tabs.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useGRStyles } from "../../goodsReceiptStyles";

const SCROLL_STEP = 180;

const TabsBar = ({ children, headRight }) => {
  const classes = useGRStyles();
  const scroller = useRef(null);
  const [more, setMore] = useState({ left: false, right: false });

  // Recompute which chevrons are usable (called on scroll, resize and tab changes).
  const sync = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setMore({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    sync();
    const el = scroller.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", sync);
      return () => window.removeEventListener("resize", sync);
    }
    // Defer sync() to the next frame instead of running it synchronously
    // inside the observer callback — a layout change triggered by that same
    // sync() (e.g. the chevrons appearing/disappearing, or a sibling section
    // resizing on the same tick as a tab switch) can otherwise re-trigger the
    // observer within the same cycle, which the browser reports as
    // "ResizeObserver loop completed with undelivered notifications." This is
    // the standard mitigation — harmless either way, just avoids the noisy
    // (dev-only) error overlay.
    let rafId = null;
    const ro = new ResizeObserver(() => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sync);
    });
    ro.observe(el);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [sync, children]);

  const slide = (dir) => {
    const el = scroller.current;
    if (el) el.scrollLeft += dir * SCROLL_STEP;
  };

  const hasArrows = more.left || more.right;

  return (
    <div className={classes.tabsBar}>
      <div className={classes.tabsBarLeft}>
        {hasArrows && (
          <IconButton className={classes.tabArrow} onClick={() => slide(-1)} disabled={!more.left} aria-label="Previous tabs">
            <ChevronLeftIcon fontSize="default" />
          </IconButton>
        )}
        <div className={classes.tabScroller} ref={scroller} onScroll={sync} role="tablist">
          {children}
        </div>
        {hasArrows && (
          <IconButton className={classes.tabArrow} onClick={() => slide(1)} disabled={!more.right} aria-label="Next tabs">
            <ChevronRightIcon fontSize="default" />
          </IconButton>
        )}
      </div>
      <div className={classes.tabsBarRight}>
        {headRight && <div className={classes.btnRow}>{headRight}</div>}
      </div>
    </div>
  );
};

export default TabsBar;
